
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import AnalyticsTracker from "../components/AnalyticsTracker";

/**
 * Keep public + embed pages that must open fast as direct imports.
 * Lazy-load heavy dashboard/business pages only.
 */
import Homepage from "features/home/pages/Homepage";
import LoginForm from "features/auth/components/LoginForm";
import SignupForm from "features/auth/components/SignupForm";
import ResetPassword from "features/auth/components/ResetPassword";
import CollectEmbed from "features/embed/pages/CollectEmbed";
import WallEmbed from "features/embed/pages/WallEmbed";

const Pricing = lazy(() => import("features/home/components/Pricing"));
const Success = lazy(() => import("features/home/components/Success"));
const Features = lazy(() => import("features/home/components/Features"));
const Resources = lazy(() => import("features/home/pages/Resources"));


const Dashboard = lazy(() => import("features/dashboard/pages/Dashboard"));
const AnalyticsDashboard = lazy(() => import("features/analytics/pages/AnalyticsDashboard"));
const MainDashboard = lazy(() =>
  import("features/Maindashboard/pages/MainDashboard")
);

const CreateNewSpace = lazy(() =>
  import("features/spaces/pages/Createnewspace")
);

const PublicReviewPage = lazy(() =>
  import("features/public/pages/PublicReviewPage")
);

const PrivacyPolicy = lazy(() =>
  import("features/legal/pages/PrivacyPolicy")
);
const TermsConditions = lazy(() =>
  import("features/legal/pages/TermsConditions")
);
const RefundPolicy = lazy(() =>
  import("features/legal/pages/RefundPolicy")
);
const NotFound = lazy(() => import("features/home/pages/NotFound"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-10 h-10 mx-auto mb-3 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
        <p className="text-sm text-gray-500">Loading page...</p>
      </div>
    </div>
  );
}

function LazyPage({ children }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function PrivatePage({ children }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
     <AnalyticsTracker />
      <Routes>
        {/* ---------------- PUBLIC SITE: direct/fast landing ---------------- */}
        <Route path="/" element={<Homepage />} />

        <Route
          path="/features"
          element={
            <LazyPage>
              <Features />
            </LazyPage>
          }
        />

        <Route
          path="/resources"
          element={
            <LazyPage>
              <Resources />
            </LazyPage>
          }
        />

        <Route
          path="/pricing"
          element={
            <LazyPage>
              <Pricing />
            </LazyPage>
          }
        />

        <Route
          path="/success"
          element={
            <LazyPage>
              <Success />
            </LazyPage>
          }
        />

        {/* ---------------- LEGAL PAGES ---------------- */}
        <Route
          path="/privacy-policy"
          element={
            <LazyPage>
              <PrivacyPolicy />
            </LazyPage>
          }
        />
         <Route
          path="*"
          element={
            <LazyPage>
              <NotFound />
            </LazyPage>
          }
        />
        <Route
          path="/terms-and-conditions"
          element={
            <LazyPage>
              <TermsConditions />
            </LazyPage>
          }
        />

        <Route
          path="/refund-policy"
          element={
            <LazyPage>
              <RefundPolicy />
            </LazyPage>
          }
        />

        {/* ---------------- AUTH: direct/fast ---------------- */}
        <Route path="/login" element={<LoginForm />} />
        <Route path="/signup" element={<SignupForm />} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route path="/update-password" element={<Navigate to="/reset" replace />} />

        {/* ---------------- PRIVATE DASHBOARD ---------------- */}
        <Route
          path="/dashboard"
          element={
            <PrivatePage>
              <LazyPage>
                <Dashboard />
              </LazyPage>
            </PrivatePage>
          }
        />


        <Route
          path="/dashboard/analytics"
          element={
            <PrivatePage>
              <LazyPage>
                <AnalyticsDashboard />
              </LazyPage>
            </PrivatePage>
          }
        />

        <Route
          path="/spaces/new"
          element={
            <PrivatePage>
              <LazyPage>
                <CreateNewSpace />
              </LazyPage>
            </PrivatePage>
          }
        />

        <Route
          path="/spaces/edit/:id"
          element={
            <PrivatePage>
              <LazyPage>
                <CreateNewSpace />
              </LazyPage>
            </PrivatePage>
          }
        />

        {/* Keep only this one dashboard route */}
        <Route
          path="/maindashboard/:spaceSlug/*"
          element={
            <PrivatePage>
              <LazyPage>
                <MainDashboard />
              </LazyPage>
            </PrivatePage>
          }
        />

        {/* ---------------- PUBLIC CUSTOMER REVIEW PAGE ---------------- */}
        <Route
          path="/request/:spaceSlug"
          element={
            <LazyPage>
              <PublicReviewPage />
            </LazyPage>
          }
        />

        {/* ---------------- EMBED ROUTES: direct/fast, no ProtectedRoute ---------------- */}
        <Route path="/embed/collect/:spaceSlug" element={<CollectEmbed />} />
        <Route path="/embed/wall/:spaceSlug" element={<WallEmbed />} />
      </Routes>
    </BrowserRouter>
  );
}
